import { FC, Fragment, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
// Hooks
import useToggle from 'hooks/useToggle';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import ConsentsAsync from 'store/consents/consentsAsync';
import { consentsActions } from 'store/consents/consentsSlice';
import { selectConsentsGroups } from 'store/consents/consentsSelectors';
import { selectQuiz } from 'store/quiz/quizzesSelectors';
import IConsent from 'models/Consent';
import ConsentsStatuses from 'types/ConsentsStatuses';
// Components
import { Checkbox } from 'components/Controls';
import { IconButton } from 'components/Buttons';
import { Tooltip } from 'components/Tooltip';
import ConsentsDetailsDialog from 'dialogs/ConsentsDetails.dialog';
// Icons
import { FaEye } from "react-icons/fa";
// i18next
import { useTranslation } from 'react-i18next';

const SectionConsents:FC = () => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const quiz = useAppSelector(selectQuiz);
  const consents = useAppSelector(selectConsentsGroups);
  const [selectedConsents, setSelectedConsents] = useState<string[]>([]);
  const [consentDetailsId, setConsentDetailsId] = useState<string | null>(null);

  const { watch, setValue } = useFormContext();

  const languageWatcher = watch('language');

  const handleSelect = (consentId: string) => {
    if (selectedConsents.includes(consentId)) {
      setSelectedConsents(prev => prev.filter(id => id !== consentId));
    } else {
      setSelectedConsents(prev => ([...prev, consentId]));
    }
  }

  useEffect(() => {
    setValue('consents', selectedConsents);
    // eslint-disable-next-line
  }, [selectedConsents]);
  
  useEffect(() => {
    // eslint-disable-next-line
    dispatch(ConsentsAsync.getConsents({ status: ConsentsStatuses.published, lang: languageWatcher, ['include.platform']: true })).unwrap()
      .then((consents:IConsent[]) => {
        const quizConsents = quiz?.consents?.filter(consentId => !!consents.find(consent => consent._id === consentId)) || [];
        const requiredConsentsIds = consents.filter(consent => consent.required).map(c => c._id);
        const set = new Set([...quizConsents, ...requiredConsentsIds]);
        setSelectedConsents([...set]);
      });
    // eslint-disable-next-line
  }, [languageWatcher]);

  useEffect(() => {
    return () => {
      dispatch(consentsActions.setInitialField('consents'));
    }
    // eslint-disable-next-line
  }, []);

  const { open, toggle } = useToggle();
  const handleCloseConsentsDetailsDialog = () => {
    setConsentDetailsId(null);
    toggle();
  }

  const consentsLabels:Record<string, { title: string; hint: string }> = {
    platform: {
      title: t('ownerPage.quizForm.platformConsentsTitle'),
      hint:  t('ownerPage.quizForm.platformConsentsHint')
    },
    brand: {
      title: t('ownerPage.quizForm.accountConsentsTitle'),
      hint:  t('ownerPage.quizForm.accountConsentsHint')
    }
  }

  if (!consents) return null;
  return (
    <Fragment>
      <div react-scroll="consents">
        <span className="divider"></span>
        <div className="p-6">
          <h2 className="text-white text-2xl leading-6 font-bold">{t('common.consents')}</h2>
        </div>
        <div className="p-6">
          {Object.keys(consents).map((key) => (
            <div key={key} className="mb-8">
              <span className="block text-white text-base font-semibold mr-3 mb-1">{consentsLabels[key].title}</span>
              <span className="block text-white text-xs mr-3 mb-3">{consentsLabels[key].hint}</span>
              {consents[key].map(consent => (
                <div key={consent._id} className="flex items-center gap-2 mb-2">
                  <Checkbox
                    label={consent.name} id={consent.name} name={consent.name}
                    checked={selectedConsents.includes(consent._id)}
                    onChange={() => handleSelect(consent._id)}
                    disabled={consent.required}
                    className="mb-0"
                  />
                  <Tooltip title="Details">
                    <IconButton
                      size="sm"
                      onClick={() => {
                        setConsentDetailsId(consent._id);
                        toggle();
                      }}
                    >
                      <FaEye />
                    </IconButton>
                  </Tooltip>
                </div>
              ))}
            </div>
          ))}
          {!Object.keys(consents).length && (
            <span className="block text-white text-sm font-semibold mr-3 mb-3">{t('ownerPage.quizForm.noConsents')}</span>
          )}
        </div>
      </div>
      
      {consentDetailsId ? (
        <ConsentsDetailsDialog open={open} onClose={handleCloseConsentsDetailsDialog} consentId={consentDetailsId} />
      ) : null}
    </Fragment>
  )
}

export default SectionConsents;
